import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { formClasses } from '../../Form';
export const zoomIn = {
    '@keyframes ui-from-zoom-in': {
        '0%': {
            transform: 'scale(0.2)',
            opacity: 0,
        },
        '100%': {
            transform: 'scale(1)',
            opacity: 1,
        },
    },
};
const componentName = 'FormItem';
const prefixName = `ui-${componentName}`;
const itemClass = `${prefixName}-item`;
const labelClass = `${itemClass}-label`;
export const formItemClasses = {
    root: prefixName,
    large: `${prefixName}-large`,
    text: `${prefixName}-text`,
    item: {
        root: itemClass,
        explain: `${itemClass}-explain`,
        explainError: `${itemClass}-explain-error`,
        explainWarning: `${itemClass}-explain-warning`,
        extra: `${itemClass}-extra`,
        extraWithExplain: `${itemClass}-extra-with-explain`,
        withHelp: `${itemClass}-with-help`,
        hasError: `${itemClass}-has-error`,
        countMessage: `${itemClass}-count-message`,
        countMessageEnd: `${itemClass}-count-message-end`,
        maxExceededIndicator: `${itemClass}-max-exceeded-indicator`,
        maxExceededIndicatorEnd: `${itemClass}-max-exceeded-indicator-end`,
    },
    label: {
        root: `${labelClass}-root`,
        container: `${labelClass}-container`,
        label: `${labelClass}-label`,
        wrapper: `${labelClass}-wrapper`,
        left: `${labelClass}-left`,
        wrap: `${labelClass}-wrap`,
        required: `${labelClass}-required`,
        optional: `${labelClass}-optional`,
        tooltip: `${labelClass}-tooltip`,
        colon: `${labelClass}-colon`,
        requiredMark: `${labelClass}-requiredMark`,
        optionalMark: `${labelClass}-optionalMark`,
        description: `${labelClass}-desc`,
    },
    layout: {
        vertical: `${prefixName}-vertical`,
        horizontal: `${prefixName}-horizontal`,
        inline: `${prefixName}-inline`,
    },
    control: {
        root: `${itemClass}-control`,
        input: `${itemClass}-control-input`,
        content: `${itemClass}-control-input-content`,
    },
};
export const resetComponent = (token) => ({
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    color: token.color.text,
    fontSize: token.fontSize.default,
    listStyle: 'none',
    fontFamily: token.fontFamily,
});
const resetForm = (token) => ({
    legend: {
        display: 'block',
        width: '100%',
        marginBottom: token.margin.large,
        padding: 0,
        color: token.color.secondary,
        fontSize: token.fontSize.default,
        lineHeight: 'inherit',
        border: 0,
        borderBottom: `${token.border.width}px ${token.border.type} ${token.border.color.default}`,
    },
    label: {
        fontSize: token.fontSize.default,
    },
    'input[type="search"]': {
        boxSizing: 'border-box',
    },
    // Position radios and checkboxes better
    'input[type="radio"], input[type="checkbox"]': {
        lineHeight: 'normal',
    },
    'input[type="file"]': {
        display: 'block',
    },
    // Make range inputs behave like textual form controls
    'input[type="range"]': {
        display: 'block',
        width: '100%',
    },
    // Make multiple select elements height not fixed
    'select[multiple], select[size]': {
        height: 'auto',
    },
    // Focus for file, radio, and checkbox
    [`input[type='file']:focus,
  input[type='radio']:focus,
  input[type='checkbox']:focus`]: {
        outline: 0,
    },
    // Adjust output element
    output: {
        display: 'block',
        paddingTop: 15,
        color: token.color.text,
        fontSize: token.fontSize.default,
        lineHeight: token.lineHeight.default,
    },
});
const genFormSize = (height) => ({
    [`.${formItemClasses.item.root}`]: {
        [`.${formItemClasses.item.root}-label > label`]: {
            height,
        },
        [`.${formItemClasses.item.root}-control-input`]: {
            minHeight: height,
        },
    },
});
const genFormStyle = (token) => ({
    [`&.${formItemClasses.root}`]: {
        ...resetComponent(token),
        ...resetForm(token),
        [`.${formItemClasses.root}-text`]: {
            display: 'inline-block',
            paddingInlineEnd: token.padding,
        },
        [`.${formItemClasses.large}`]: {
            ...genFormSize(token.control.height.large),
        },
    },
});
const genFormItemStyle = (token) => 
// const iconCls = '';
({
    [`.${formItemClasses.item.root}`]: {
        ...resetComponent(token),
        marginBottom: token.margin.default,
        verticalAlign: 'top',
        [`&.${formItemClasses.item.withHelp}`]: {
            transition: 'none',
        },
        // ==============================================================
        // =                            Label                           =
        // ==============================================================
        [`.${formItemClasses.label.root}`]: {
            [`.${formItemClasses.label.container}`]: {
                display: 'flex',
            },
            [`.${formItemClasses.label.wrapper}`]: {
                display: 'inline-flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '4px',
                [`.${formItemClasses.label.label}`]: {
                    color: token.label.color,
                    fontSize: token.fontSize.default,
                    height: '20px',
                    lineHeight: '20px',
                },
                [`.${formItemClasses.label.requiredMark}`]: {
                    color: token.color.error,
                    height: '20px',
                    lineHeight: '24px',
                    overflow: 'hidden',
                    flexShrink: 0,
                },
                [`.${formItemClasses.label.optionalMark}`]: {
                    color: token.color.optional,
                },
                [`&.${formItemClasses.label.colon}::after`]: {
                    content: '":"',
                    height: '20px',
                    lineHeight: '18px',
                },
            },
            [`&.${formItemClasses.label.left}`]: {
                textAlign: 'start',
            },
            [`&.${formItemClasses.label.wrap}`]: {
                overflow: 'unset',
                whiteSpace: 'unset',
            },
        },
        // ==============================================================
        // =                            Input                           =
        // ==============================================================
        [`.${formItemClasses.control.root}`]: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            [`&:first-of-type:not([class^="MuiGrid"]):not([class*="MuiGrid"])`]: {
                width: '100%',
            },
            [`.${formItemClasses.control.input}`]: {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                minHeight: token.control.height.default,
                [`.${formItemClasses.control.content}`]: {
                    flex: 'auto',
                    maxWidth: '100%',
                },
            },
        },
    },
    // ==============================================================
    // =                           Explain                          =
    // ==============================================================
    [`.${formItemClasses.item.explain}, .${formItemClasses.item.extra}`]: {
        clear: 'both',
        color: token.color.secondary,
        fontSize: token.fontSize.small,
        transition: `color ${token.motion.duration.mid} ${token.motion.type.easeOut}`, // sync input color transition
    },
    [`.${formItemClasses.item.explain}-connected`]: {
        width: '100%',
    },
    [`.${formItemClasses.item.explainError}-container`]: {
        display: 'flex',
        flexWrap: 'nowrap',
        marginTop: 8,
        alignItems: 'center',
        [`&.${formItemClasses.item.maxExceededIndicatorEnd}`]: {
            textAlign: 'right',
        },
    },
    [`.${formItemClasses.item.countMessage}`]: {
        color: token.count.color,
        lineHeight: token.count.lineHeight,
        fontFamily: token.fontFamily,
        fontSize: token.count.fontSize,
        fontWeight: token.count.fontWeight,
        marginTop: token.margin.xxs,
    },
    [`.${formItemClasses.item.countMessageEnd}`]: {
        textAlign: 'right',
    },
    [`.${formItemClasses.item.explainError}-icon`]: {
        display: 'inline-block',
        height: 14,
        lineHeight: token.lineHeight.explain,
        color: token.color.error,
        marginRight: 4,
    },
    [`.${formClasses.item.explain}`]: {
        [`.${formClasses.item.explainError}`]: {
            color: token.color.error,
        },
        [`.${formClasses.item.explainWarning},`]: {
            color: token.color.warning,
        },
    },
    [`&.${formItemClasses.item.withHelp} .${formItemClasses.item.explain}`]: {
        height: 'auto',
        lineHeight: token.lineHeight.explain,
        opacity: 1,
    },
    [`&.${formItemClasses.item.hasError}`]: {
        [`> input`]: {
            borderColor: `${token.border.color.error}`,
        },
    },
    [`.${formItemClasses.item.extra}`]: {
        marginTop: 4,
    },
    [`.${formItemClasses.item.extraWithExplain}`]: {
        marginTop: 4,
    },
});
const genHorizontalStyle = () => ({
    [`&.${formItemClasses.layout.horizontal}`]: {
        [`.${formItemClasses.label.root}`]: {
            flexGrow: 0,
            alignItems: 'flex-start',
            [`.${formItemClasses.label.wrapper}`]: {
                width: '100%',
                minHeight: '32px',
                paddingRight: '8px',
            },
        },
        [`.${formItemClasses.control.root}`]: {
            flex: '1 1 0',
            minWidth: 0,
        },
    },
});
const genInlineStyle = (token) => ({
    [`&.${formItemClasses.layout.inline}`]: {
        display: 'inline-flex',
        flexWrap: 'wrap',
        [`.${formItemClasses.item.root}`]: {
            flex: 'none',
            flexWrap: 'nowrap',
            marginInlineEnd: token.margin.default,
            marginBottom: 0,
            [`.${formItemClasses.label.root}`]: {
                [`.${formItemClasses.label.wrapper}`]: {
                    minHeight: '32px',
                    paddingRight: '8px',
                },
            },
            [`&.${formItemClasses.item.withHelp}`]: {
                marginBottom: token.margin.default,
            },
            [`> .${formItemClasses.label.root},
        > .${formItemClasses.control.root}`]: {
                display: 'inline-block',
                verticalAlign: 'top',
            },
            [`> .${formItemClasses.label.root}`]: {
                flex: 'none',
            },
            [`.${formItemClasses.root}-text`]: {
                display: 'inline-block',
            },
        },
    },
});
const makeVerticalLayoutLabel = (token) => ({
    margin: 0,
    padding: `0 0 ${token.padding}px`,
    whiteSpace: 'initial',
    textAlign: 'start',
});
const makeVerticalLayout = (token) => ({
    [`.${formItemClasses.item.root} .${formItemClasses.label.root}`]: {
        ...makeVerticalLayoutLabel(token),
    },
    [`&.${formItemClasses.root}`]: {
        [`.${formItemClasses.item.root}`]: {
            flexWrap: 'wrap',
            [`.${formItemClasses.label.root},
          .${formItemClasses.control.root}`]: {
                flex: '0 0 100%',
                maxWidth: '100%',
            },
        },
    },
});
const genVerticalStyle = (token) => ({
    [`&.${formItemClasses.root}-vertical`]: {
        [`.${formItemClasses.item.root}`]: {
            [`.${formItemClasses.item.root}-grid`]: {
                flexDirection: 'column',
            },
            [`.${formItemClasses.label.root}`]: {
                [`.${formItemClasses.label.container}`]: {
                    justifyContent: 'space-between',
                },
                [`.${formItemClasses.label.wrapper}`]: {
                    justifyContent: 'flex-start',
                    paddingBottom: '4px',
                    width: 'auto',
                    height: 'auto',
                    lineHeight: '20px',
                },
            },
            [`.${formItemClasses.label.description}`]: {
                paddingBottom: '4px',
                ...token.label.description,
            },
            [`.${formItemClasses.control.root}`]: {
                width: '100%',
            },
        },
    },
    [`&.${formItemClasses.root}-vertical .${formItemClasses.item.root}-label`]: {
        ...makeVerticalLayoutLabel(token),
    },
    [`@media (max-width: ${token.screen.max.sm}px)`]: {
        ...makeVerticalLayout(token),
    },
});
// ============================== Export ==============================
export function getStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token] = generateDynamicThemeToken({
        componentName: 'Form',
        theme,
        dynamicToken,
    });
    return {
        ...genFormStyle(token),
        ...genFormItemStyle(token),
        ...genHorizontalStyle(),
        ...genInlineStyle(token),
        ...genVerticalStyle(token),
    };
}
